import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../data/auth";
import { useStore } from "../data/store";
import T from "../styles/theme";
import Icon from "../components/Icon";
import {Btn, Badge, Select } from '../components/UI';
import apiFetch from '../data/api';
import { IMPORT_FIELDS, autoMapColumns } from '../data/importMapper';

/* ═══════════════════════════════════════════════════════
   AI-GUIDED ONBOARDING
   Step 0: Welcome + Create Org
   Step 1: AI Pipeline Builder (conversational)
   Step 2: Import Contacts (CSV with AI mapping)
   Step 3: Phone Setup (Twilio)
   Step 4: Ready!
   ═══════════════════════════════════════════════════════ */

import COMPANY_TYPES, { PresetIcon } from '../data/CompanyPresets';

const STEPS = [
  { label: "Your Org", icon: "building" },
  { label: "Company Type", icon: "briefcase" },
  { label: "Pipeline", icon: "pipeline" },
  { label: "Import", icon: "upload" },
  { label: "Ready!", icon: "check" },
];

// ── Industry pipeline templates ──
const PIPELINE_TEMPLATES = {
  saas: { name: "SaaS Sales", stages: [
    { name: "Discovery", prob: 10, color: "#535C6E" },
    { name: "Qualification", prob: 25, color: "#FFB547" },
    { name: "Demo", prob: 40, color: "#4059AD" },
    { name: "Proposal", prob: 60, color: "#7C5CFC" },
    { name: "Negotiation", prob: 80, color: "#FAFAFA" },
    { name: "Closed Won", prob: 100, color: "#00D68F" },
  ]},
  realestate: { name: "Real Estate", stages: [
    { name: "Lead", prob: 10, color: "#535C6E" },
    { name: "Showing", prob: 30, color: "#FFB547" },
    { name: "Offer", prob: 50, color: "#7C5CFC" },
    { name: "Under Contract", prob: 75, color: "#FAFAFA" },
    { name: "Closing", prob: 90, color: "#4059AD" },
    { name: "Closed", prob: 100, color: "#00D68F" },
  ]},
  agency: { name: "Agency", stages: [
    { name: "Inquiry", prob: 10, color: "#535C6E" },
    { name: "Meeting", prob: 30, color: "#FFB547" },
    { name: "Proposal", prob: 50, color: "#7C5CFC" },
    { name: "Contract", prob: 80, color: "#FAFAFA" },
    { name: "Won", prob: 100, color: "#00D68F" },
  ]},
  consulting: { name: "Consulting", stages: [
    { name: "Discovery Call", prob: 15, color: "#535C6E" },
    { name: "Needs Assessment", prob: 30, color: "#FFB547" },
    { name: "Solution Design", prob: 50, color: "#7C5CFC" },
    { name: "Proposal", prob: 70, color: "#FAFAFA" },
    { name: "Negotiation", prob: 85, color: "#4059AD" },
    { name: "Closed Won", prob: 100, color: "#00D68F" },
  ]},
  ecommerce: { name: "E-Commerce", stages: [
    { name: "Lead", prob: 10, color: "#535C6E" },
    { name: "Contacted", prob: 25, color: "#FFB547" },
    { name: "Quote Sent", prob: 50, color: "#7C5CFC" },
    { name: "Order", prob: 90, color: "#FAFAFA" },
    { name: "Fulfilled", prob: 100, color: "#00D68F" },
  ]},
  custom: { name: "Custom", stages: [
    { name: "Stage 1", prob: 20, color: "#535C6E" },
    { name: "Stage 2", prob: 40, color: "#FFB547" },
    { name: "Stage 3", prob: 60, color: "#7C5CFC" },
    { name: "Stage 4", prob: 80, color: "#FAFAFA" },
    { name: "Closed Won", prob: 100, color: "#00D68F" },
  ]},
};

const STAGE_COLOR_OPTIONS = ["#535C6E", "#FFB547", "#7C5CFC", "#FAFAFA", "#4059AD", "#00D68F", "#E53935", "#FF6D00", "#00B8D4", "#C51162"];

// CSV auto-mapping uses the shared scored bipartite mapper from
// src/data/importMapper.js — replaces the local first-substring-wins
// FIELD_MAP/autoMapColumns. Field ids in this file now match the shared
// IMPORT_FIELDS list (e.g. "phone" + "company", not the legacy "ph" / "co").

// CSV/TSV parser (RFC 4180-aware, handles multi-line quoted fields).
// Single-pass character walker that maintains inQ state across newlines.
// Previous implementation split on \n before walking, fragmenting any
// multi-line quoted cell — same root cause as the ListsPage import
// regression that produced ghost rows in production. Preserves the
// existing tab-vs-comma delimiter auto-detection and header quote-strip.
function parseCSV(text) {
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  // Sniff delimiter: scan first line outside quotes for a tab; default to comma.
  let delim = ",";
  {
    let inQ = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '"') { inQ = !inQ; continue; }
      if (!inQ && ch === '\t') { delim = "\t"; break; }
      if (!inQ && (ch === '\n' || ch === '\r')) break;
    }
  }

  const rows = [];
  let row = [];
  let cur = "";
  let inQ = false;
  let i = 0;
  const len = text.length;

  while (i < len) {
    const ch = text[i];

    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cur += '"'; i += 2; continue; }
        inQ = false; i++; continue;
      }
      cur += ch; i++; continue;
    }

    if (ch === '"') { inQ = true; i++; continue; }
    if (ch === delim) { row.push(cur.trim()); cur = ""; i++; continue; }
    if (ch === '\r') {
      row.push(cur.trim()); rows.push(row); row = []; cur = "";
      if (text[i + 1] === '\n') i += 2; else i++;
      continue;
    }
    if (ch === '\n') {
      row.push(cur.trim()); rows.push(row); row = []; cur = "";
      i++; continue;
    }
    cur += ch; i++;
  }

  if (cur.length > 0 || row.length > 0) {
    row.push(cur.trim());
    rows.push(row);
  }

  const nonEmpty = rows.filter(r => r.some(c => c !== ""));
  if (nonEmpty.length < 2) return { headers: [], rows: [] };

  // Strip quote wrappers from headers (preserves prior behavior).
  const headers = nonEmpty[0].map(h => h.replace(/^["']|["']$/g, ""));
  return { headers, rows: nonEmpty.slice(1) };
}

export default function OnboardingPage() {
  const { currentUser, session, createOrg, completeOnboarding, updateOnboardingStep, onboardingStep } = useAuth();
  const store = useStore();
  const [step, setStep] = useState(onboardingStep || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Step 0 state
  const [orgName, setOrgName] = useState("");
  const [domain, setDomain] = useState("");
  const [firstName, setFirstName] = useState(currentUser?.firstName || "");
  const [lastName, setLastName] = useState(currentUser?.lastName || "");

  // Step 1 state — Company Type
  const [companyType, setCompanyType] = useState(currentUser?._companyType || null);

  // Step 2 state — Pipeline
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [editingStage, setEditingStage] = useState(null);
  const [newStageName, setNewStageName] = useState("");
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [industryInput, setIndustryInput] = useState("");

  // Step 2 state — CSV Import
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Step 3 state — Phone
  const [provisionStatus, setProvisionStatus] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [searchType, setSearchType] = useState("local");
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [purchasedNumbers, setPurchasedNumbers] = useState([]);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [buyingNumber, setBuyingNumber] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (onboardingStep > step) setStep(onboardingStep);
  }, [onboardingStep]);

  const orgId = currentUser?._orgId;

  // ═══ STEP 0: Create Org ═══
  async function handleCreateOrg() {
    if (!orgName.trim()) { setError("Organization name is required"); return; }
    setLoading(true); setError("");
    const result = await createOrg({ orgName: orgName.trim(), companyDomain: domain.trim(), firstName, lastName });
    setLoading(false);
    if (result.success) {
      setStep(1);
      await updateOnboardingStep(1);
    } else {
      setError(result.error || "Failed to create organization");
    }
  }

  // ═══ STEP 1: Company Type ═══
  async function handleSelectCompanyType(typeKey) {
    setCompanyType(typeKey);
    setLoading(true); setError("");
    try {
      const preset = COMPANY_TYPES[typeKey];
      if (!preset) { setError("Invalid company type"); setLoading(false); return; }

      // Auto-load pipeline stages from preset
      setPipelineStages(preset.pipeline.stages.map((s, i) => ({ ...s, id: i })));
      setSelectedTemplate(typeKey);

      // Save company type to org
      if (orgId) {
        await apiFetch("/api/org/update", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orgId,
            updates: {
              company_type: typeKey,
              kpi_targets: preset.kpiTargets,
              lead_scoring: preset.leadScoring,
              dashboard_presets: preset.dashboardPresets,
            },
          }),
        }).catch(() => {
          // Non-critical — save locally
          localStorage.setItem("cf_company_type", typeKey);
          localStorage.setItem("cf_kpi_targets", JSON.stringify(preset.kpiTargets));
        });
      }

      // Mark checklist items
      const existing = JSON.parse(localStorage.getItem("cf_onboarding_checklist") || "{}");
      existing.company_type = true;
      localStorage.setItem("cf_onboarding_checklist", JSON.stringify(existing));
      localStorage.setItem("cf_company_type", typeKey);

      await updateOnboardingStep(2);
      setStep(2);
    } catch (e) {
      setError("Failed to save. You can continue anyway.");
      setStep(2);
    }
    setLoading(false);
  }

  // ═══ STEP 2: Pipeline ═══
  function selectTemplate(key) {
    setSelectedTemplate(key);
    setPipelineStages(PIPELINE_TEMPLATES[key].stages.map((s, i) => ({ ...s, id: i })));
  }

  function updateStage(id, field, value) {
    setPipelineStages(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  }

  function removeStage(id) {
    setPipelineStages(prev => prev.filter(s => s.id !== id));
  }

  function addStage() {
    const maxId = pipelineStages.reduce((m, s) => Math.max(m, s.id), 0);
    const insertAt = pipelineStages.length > 0 ? pipelineStages.length - 1 : 0; // Before last stage
    const newStage = { id: maxId + 1, name: newStageName || "New Stage", prob: 50, color: STAGE_COLOR_OPTIONS[pipelineStages.length % STAGE_COLOR_OPTIONS.length] };
    const updated = [...pipelineStages];
    updated.splice(insertAt, 0, newStage);
    setPipelineStages(updated);
    setNewStageName("");
  }

  function moveStage(id, dir) {
    const idx = pipelineStages.findIndex(s => s.id === id);
    if ((dir === -1 && idx === 0) || (dir === 1 && idx === pipelineStages.length - 1)) return;
    const updated = [...pipelineStages];
    [updated[idx], updated[idx + dir]] = [updated[idx + dir], updated[idx]];
    setPipelineStages(updated);
  }

  async function askAiForPipeline() {
    if (!industryInput.trim()) return;
    setAiSuggesting(true); setError("");
    try {
      const resp = await apiFetch("/api/ai/specialty", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "simple_chat",
          command: `Create a sales pipeline for a ${industryInput} company. Return ONLY a JSON array of stages like: [{"name": "Stage Name", "prob": 20, "color": "#hex"}]. Include 4-7 stages. Last stage should be the win/close stage with prob 100. Use these colors: ${STAGE_COLOR_OPTIONS.join(", ")}`,
          orgId: orgId,
          context: { contacts: [], deals: [], tasks: [], team: [] },
        }),
      });
      const data = await resp.json();
      if (data.reply) {
        try {
          const jsonMatch = data.reply.match(/\[[\s\S]*\]/);
          if (jsonMatch) {
            const stages = JSON.parse(jsonMatch[0]);
            setPipelineStages(stages.map((s, i) => ({ ...s, id: i })));
            setSelectedTemplate("ai");
          }
        } catch { setError("AI returned an invalid format. Try selecting a template instead."); }
      }
    } catch (e) { setError("AI is unavailable. Select a template instead."); }
    setAiSuggesting(false);
  }

  function savePipeline() {
    if (pipelineStages.length < 2) { setError("Pipeline needs at least 2 stages"); return; }
    if (store?.setPipelineStages) {
      store.setPipelineStages(pipelineStages.map((s, i) => ({ ...s, id: i })));
    }
    // Also save to Supabase directly for new orgs where store may not be ready
    const orgId = currentUser?._orgId;
    if (orgId) {
      apiFetch("/api/org/pipeline", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgId, stages: pipelineStages.map((s, i) => ({ ...s, id: i })) }),
      }).catch(() => {});
    }
    // Mark checklist
    const existing = JSON.parse(localStorage.getItem("cf_onboarding_checklist") || "{}");
    existing.pipeline = true;
    localStorage.setItem("cf_onboarding_checklist", JSON.stringify(existing));
    updateOnboardingStep(3);
    setStep(3);
    setError("");
  }

  // ═══ STEP 2: CSV Import ═══
  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    setError("");
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const { headers, rows } = parseCSV(text);
      if (headers.length === 0) { setError("Could not parse CSV"); return; }
      setCsvData({ headers, rows });
      // Shared scored mapper. Returns { colIdx: fieldId }; this page's state
      // is the inverse shape ({ fieldId: colIdx }), so flip it.
      const customs = (store?.customFields?.contacts || []).filter(f => f.type !== "calculation");
      const autoMapped = autoMapColumns(headers, rows.slice(0, 20), customs);
      const inverted = {};
      for (const [colIdx, fieldId] of Object.entries(autoMapped)) {
        inverted[fieldId] = parseInt(colIdx, 10);
      }
      setColumnMapping(inverted);
      // Preview first 5 rows with mapping applied
      updatePreview(rows, inverted, headers);
    };
    reader.readAsText(file);
  }

  function updatePreview(rows, mapping, headers) {
    const preview = (rows || csvData?.rows || []).slice(0, 5).map(row => {
      const contact = {};
      for (const [field, colIdx] of Object.entries(mapping || columnMapping)) {
        contact[field] = row[colIdx] || "";
      }
      return contact;
    });
    setImportPreview(preview);
  }

  function setMapping(field, colIdx) {
    const updated = { ...columnMapping };
    if (colIdx === -1) { delete updated[field]; }
    else { updated[field] = colIdx; }
    setColumnMapping(updated);
    updatePreview(csvData?.rows, updated, csvData?.headers);
  }

  async function handleImport() {
    if (!csvData || !columnMapping.fn) { setError("At least First Name must be mapped"); return; }
    setImporting(true); setError("");

    if (csvData.rows.length > 10000) { setError("CSV too large — max 10,000 contacts per import"); setImporting(false); return; }
    const contacts = csvData.rows.map(row => {
      const c = {};
      for (const [field, colIdx] of Object.entries(columnMapping)) {
        c[field] = (row[colIdx] || "").replace(/^["']|["']$/g, "").trim();
      }
      return c;
    }).filter(c => c.fn); // Must have first name

    // Pre-pass: batch-resolve every distinct company name to a company_id.
    // ONE round-trip for company creation regardless of how many CSV rows
    // share each company. Per-row lookup below uses the returned map.
    // Field id "company" matches the shared IMPORT_FIELDS list.
    const companyNames = contacts.map(c => c.company).filter(Boolean);
    const companyMap = await store.findOrCreateCompaniesBulk(companyNames);

    // Import in batches
    let imported = 0;
    let failed = 0;
    const batchSize = 50;

    for (let i = 0; i < contacts.length; i += batchSize) {
      const batch = contacts.slice(i, i + batchSize);
      try {
        for (const c of batch) {
          const companyText = c.company || "";
          const normalized = companyText.trim().toLowerCase();
          const companyId = normalized ? companyMap[normalized] || null : null;
          // Field ids "phone" and "company" come from the shared IMPORT_FIELDS;
          // the store's createContact accepts either short (ph/co) or long
          // (phone/company) keys via dbInsertContact's fallback logic.
          // _skipActivity suppresses per-row contact_created writes; we emit
          // one batch 'import' activity at the end instead.
          await store.createContact({
            fn: c.fn || "",
            ln: c.ln || "",
            email: c.email || "",
            ph: c.phone || "",
            co: companyText,
            company_id: companyId,
            title: c.title || "",
            city: c.city || "",
            state: c.state || "",
            industry: c.industry || "",
            source: c.source || "CSV Import",
            status: "active",
            score: parseInt(c.score) || 0,
            leadStage: "New",
            _skipActivity: true,
          });
          imported++;
        }
      } catch (e) {
        failed += batch.length;
      }
    }

    // Single batch activity for the import. Owner credit without
    // flooding the panel with one row per imported contact.
    if (imported > 0) {
      try {
        await store.addActivity({
          type: "import",
          title: `Imported ${imported} contact${imported === 1 ? "" : "s"}`,
          notes: "via CSV onboarding",
        });
      } catch (e) { console.warn("[Onboarding] import activity write failed:", e?.message); }
    }

    setImportResult({ imported, failed, total: contacts.length });
    setImporting(false);
  }

  // ═══ STEP 3: Phone ═══
  async function handleProvision() {
    if (!orgId) { setError("No org ID"); return; }
    setLoading(true); setError(""); setProvisionStatus("Creating your calling system...");
    try {
      const resp = await apiFetch("/api/twilio/provision", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgId, orgName: orgName || currentUser?.firstName + "'s Org" }),
      });
      const data = await resp.json();
      if (data.success) {
        setProvisionStatus("Calling system ready!");
        setTimeout(() => {}, 1000);
      } else {
        setError(data.error || "Provisioning failed");
        setProvisionStatus("");
      }
    } catch (e) {
      setError(e.message);
      setProvisionStatus("");
    }
    setLoading(false);
  }

  async function handleSearchNumbers() {
    setSearchingNumbers(true); setError(""); setAvailableNumbers([]);
    try {
      const resp = await apiFetch("/api/twilio/numbers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: searchType === "toll_free" ? "search_toll_free" : "search", orgId, areaCode: areaCode || undefined }),
      });
      const data = await resp.json();
      if (data.numbers) setAvailableNumbers(data.numbers);
      else setError(data.error || "No numbers found");
    } catch (e) { setError(e.message); }
    setSearchingNumbers(false);
  }

  async function handleBuyNumber(number) {
    setBuyingNumber(number.phoneNumber); setError("");
    try {
      const resp = await apiFetch("/api/twilio/numbers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "purchase", orgId, phoneNumber: number.phoneNumber }),
      });
      const data = await resp.json();
      if (data.success) {
        setPurchasedNumbers(prev => [...prev, { ...number, ...data }]);
        setAvailableNumbers(prev => prev.filter(n => n.phoneNumber !== number.phoneNumber));
      } else { setError(data.error || "Purchase failed"); }
    } catch (e) { setError(e.message); }
    setBuyingNumber(null);
  }

  // ═══ STEP 4: Finish ═══
  async function handleFinish() {
    setLoading(true);
    await completeOnboarding();
    setLoading(false);
    window.history.pushState({}, "", "/dashboard");
    window.location.reload();
  }

  // ═══ Styles ═══
  const inputStyle = {
    width: "100%", padding: "12px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
    background: "rgba(255,255,255,0.04)", color: "#FAFAFA", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit",
  };
  const labelStyle = { display: "block", fontSize: 12, fontWeight: 400, color: "rgba(217,219,221,0.5)", marginBottom: 6, letterSpacing: ".03em" };
  const cardBtn = (selected) => ({
    padding: "14px 16px", borderRadius: 8, border: "1px solid " + (selected ? "rgba(250,250,250,0.25)" : "rgba(217,219,221,0.12)"),
    background: selected ? "rgba(250,250,250,0.06)" : "transparent", cursor: "pointer", textAlign: "left",
    transition: "all 0.15s",
  });

  return (
    <div style={{ minHeight: "100vh", background: "#2C2E33", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 640, background: "#33363D", borderRadius: 12, border: "1px solid rgba(217,219,221,0.08)", overflow: "hidden", boxShadow: "0 12px 60px rgba(0,0,0,0.3)" }}>
        {/* Header */}
        <div style={{ padding: "28px 32px 20px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 4 }}>
            <img src="/coreflow-logo-white.png" alt="Coreflow" style={{ height: 20, objectFit: "contain" }} />
          </div>
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 20, fontWeight: 400, color: "#FAFAFA" }}>
              {step === 0 ? "Welcome to Coreflow" : step === 1 ? "What's Your Business?" : step === 4 ? "You're All Set!" : "Setting Up " + (orgName || "Your CRM")}
            </div>
            <div style={{ fontSize: 12, color: "rgba(217,219,221,0.45)", marginTop: 4 }}>
              {step === 0 ? "We'll get you set up in about 2 minutes" : step === 4 ? "Your CRM is ready to go" : `Step ${step + 1} of ${STEPS.length}`}
            </div>
          </div>
        </div>

        {/* Progress */}
        <div style={{ display: "flex", padding: "14px 32px", gap: 4, borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
          {STEPS.map((s, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                background: i <= step ? "rgba(250,250,250,0.12)" : "transparent", color: i <= step ? "#FAFAFA" : "rgba(217,219,221,0.3)",
                fontSize: 11, fontWeight: 400, border: "1px solid " + (i <= step ? "rgba(250,250,250,0.25)" : "rgba(217,219,221,0.12)"),
              }}>
                {i < step ? "✓" : i + 1}
              </div>
              <div style={{ fontSize: 8, fontWeight: 400, color: i <= step ? "#FAFAFA" : "rgba(217,219,221,0.3)", letterSpacing: ".06em", textTransform: "uppercase" }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: "24px 32px", maxHeight: "60vh", overflow: "auto" }}>
          {error && (
            <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(139,69,87,0.1)", color: "#E53935", fontSize: 12, marginBottom: 16, border: "1px solid rgba(139,69,87,0.2)" }}>
              {error}
            </div>
          )}

          {/* ════ STEP 0: Create Org ════ */}
          {step === 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ padding: "14px 16px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(217,219,221,0.08)" }}>
                <div style={{ fontSize: 13, color: "rgba(217,219,221,0.7)", lineHeight: 1.6 }}>
                  Hey! I'm Core, your AI assistant. I'll walk you through setting up your CRM in about 2 minutes. Let's start with the basics.
                </div>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>First Name</label>
                  <input value={firstName} onChange={e => setFirstName(e.target.value)} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Last Name</label>
                  <input value={lastName} onChange={e => setLastName(e.target.value)} style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Organization Name *</label>
                <input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Acme Sales Co" style={inputStyle} autoFocus />
              </div>
              <div>
                <label style={labelStyle}>Company Domain (optional)</label>
                <input value={domain} onChange={e => setDomain(e.target.value)} placeholder="acme.com" style={inputStyle} />
              </div>
              <Btn primary onClick={handleCreateOrg} style={{ width: "100%", padding: "14px", fontSize: 14, borderRadius: 8, justifyContent: "center", background: "#FAFAFA", color: "#2C2E33", border: "none" }}>
                {loading ? "Creating..." : "Continue →"}
              </Btn>
            </div>
          )}

          {/* ════ STEP 1: Company Type ════ */}
          {step === 1 && (
            <div>
              <div style={{ padding: "14px 16px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid " + "rgba(217,219,221,0.08)", marginBottom: 20 }}>
                <div style={{ fontSize: 13, color: "#FAFAFA", lineHeight: 1.6 }}>
                  What type of business is <strong>{orgName || "your company"}</strong>? This helps us pre-configure your pipeline, KPI targets, lead scoring, and templates.
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                {Object.entries(COMPANY_TYPES).map(([key, type]) => (
                  <div
                    key={key}
                    onClick={() => !loading && setCompanyType(key)}
                    style={{
                      padding: "16px", borderRadius: 8, cursor: "pointer",
                      border: `2px solid ${companyType === key ? "rgba(250,250,250,0.2)" : "rgba(217,219,221,0.12)"}`,
                      background: companyType === key ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.04)",
                      transition: "all .15s",
                    }}
                    onMouseEnter={e => { if (companyType !== key) e.currentTarget.style.borderColor = "rgba(217,219,221,0.25)"; }}
                    onMouseLeave={e => { if (companyType !== key) e.currentTarget.style.borderColor = "rgba(217,219,221,0.12)"; }}
                  >
                    <div style={{ marginBottom: 6 }}><PresetIcon name={type.icon} size={24} color={companyType === key ? "rgba(250,250,250,0.2)" : "rgba(217,219,221,0.6)"} /></div>
                    <div style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA", marginBottom: 2 }}>{type.label}</div>
                    <div style={{ fontSize: 11, color: "rgba(217,219,221,0.4)", lineHeight: 1.4 }}>{type.description}</div>
                  </div>
                ))}
              </div>

              {companyType && COMPANY_TYPES[companyType] && (
                <div style={{ padding: "14px 16px", borderRadius: 8, background: "#00D68F08", border: "1px solid #00D68F20", marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 400, color: "#FAFAFA", marginBottom: 8 }}>What we'll configure for you:</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {["Pipeline stages", "KPI targets", "Lead scoring", "Email templates", "SMS templates", "Dashboard layout"].map(item => (
                      <span key={item} style={{ padding: "4px 10px", borderRadius: 8, background: "#00D68F12", fontSize: 10, fontWeight: 400, color: "#00D68F" }}>✓ {item}</span>
                    ))}
                  </div>
                </div>
              )}

              {error && <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(239,68,68,0.12)", color: "#E53935", fontSize: 12, marginBottom: 12 }}>{error}</div>}

              <div style={{ display: "flex", gap: 10 }}>
                <Btn primary onClick={() => companyType && handleSelectCompanyType(companyType)} disabled={!companyType || loading}
                  style={{ flex: 1, padding: "14px 20px", fontSize: 14, borderRadius: 8, opacity: companyType ? 1 : 0.4, background: "#FAFAFA", color: "#2C2E33", border: "none" }}>
                  {loading ? "Configuring..." : "Apply & Continue →"}
                </Btn>
                <button onClick={() => { setStep(2); updateOnboardingStep(2); }}
                  style={{ padding: "14px 20px", borderRadius: 8, border: "1px solid " + "rgba(217,219,221,0.12)", background: "transparent", fontSize: 12, color: "rgba(217,219,221,0.4)", cursor: "pointer", fontFamily: "inherit" }}>
                  Skip
                </button>
              </div>
            </div>
          )}

          {/* ════ STEP 2: Pipeline Builder ════ */}
          {step === 2 && (
            <div>
              <div style={{ padding: "14px 16px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid " + "rgba(217,219,221,0.08)", marginBottom: 20 }}>
                <div style={{ fontSize: 13, color: "#FAFAFA", lineHeight: 1.6 }}>
                  {companyType ? (
                    <>We've pre-loaded a <strong>{COMPANY_TYPES[companyType]?.label}</strong> pipeline for you. Customize it below or pick a different template.</>
                  ) : (
                    <>Great, <strong>{orgName}</strong> is set up! Now let's build your sales pipeline. Pick a template or describe your business and I'll create one for you.</>
                  )}
                </div>
              </div>

              {/* AI suggestion input */}
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <input value={industryInput} onChange={e => setIndustryInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && askAiForPipeline()}
                  placeholder="Describe your business... e.g. 'B2B SaaS selling to enterprise'"
                  style={{ ...inputStyle, flex: 1 }} />
                <button onClick={askAiForPipeline} disabled={aiSuggesting} style={{
                  padding: "12px 20px", borderRadius: 8, border: "none", flexShrink: 0,
                  background: aiSuggesting ? "rgba(217,219,221,0.2)" : "#FAFAFA",
                  color: aiSuggesting ? "#FAFAFA" : "#2C2E33", fontSize: 12, fontWeight: 400, cursor: aiSuggesting ? "wait" : "pointer",
                }}>
                  {aiSuggesting ? "..." : "AI Build"}
                </button>
              </div>

              {/* Templates */}
              {!selectedTemplate && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
                  {Object.entries(PIPELINE_TEMPLATES).filter(([k]) => k !== "custom").map(([key, tmpl]) => (
                    <button key={key} onClick={() => selectTemplate(key)} style={cardBtn(selectedTemplate === key)}>
                      <div style={{ fontSize: 12, fontWeight: 400, color: "#FAFAFA" }}>{tmpl.name}</div>
                      <div style={{ fontSize: 10, color: "rgba(217,219,221,0.4)", marginTop: 2 }}>{tmpl.stages.length} stages</div>
                    </button>
                  ))}
                </div>
              )}

              {/* Stage editor */}
              {pipelineStages.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 400, color: "rgba(217,219,221,0.4)", marginBottom: 8, letterSpacing: ".06em" }}>YOUR PIPELINE</div>
                  {pipelineStages.map((stage, i) => (
                    <div key={stage.id} style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", marginBottom: 4,
                      borderRadius: 8, border: "1px solid " + "rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.04)", backdropFilter: "blur(16px) saturate(1.4)", WebkitBackdropFilter: "blur(16px) saturate(1.4)",
                    }}>
                      <div style={{ width: 14, height: 14, borderRadius: 8, background: stage.color, flexShrink: 0 }} />
                      {editingStage === stage.id ? (
                        <input value={stage.name} autoFocus onChange={e => updateStage(stage.id, "name", e.target.value)}
                          onBlur={() => setEditingStage(null)} onKeyDown={e => e.key === "Enter" && setEditingStage(null)}
                          style={{ flex: 1, padding: "4px 8px", borderRadius: 8, border: "1px solid " + "rgba(250,250,250,0.2)", background: "#33363D", color: "#FAFAFA", fontSize: 13, outline: "none" }} />
                      ) : (
                        <div onClick={() => setEditingStage(stage.id)} style={{ flex: 1, fontSize: 13, fontWeight: 400, color: "#FAFAFA", cursor: "pointer" }}>
                          {stage.name}
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: "rgba(217,219,221,0.4)", width: 40, textAlign: "right" }}>{stage.prob}%</div>
                      <div style={{ display: "flex", gap: 2 }}>
                        <button onClick={() => moveStage(stage.id, -1)} style={{ background: "none", border: "none", color: "rgba(217,219,221,0.4)", cursor: "pointer", padding: 2, fontSize: 10 }}>▲</button>
                        <button onClick={() => moveStage(stage.id, 1)} style={{ background: "none", border: "none", color: "rgba(217,219,221,0.4)", cursor: "pointer", padding: 2, fontSize: 10 }}>▼</button>
                        {pipelineStages.length > 2 && (
                          <button onClick={() => removeStage(stage.id)} style={{ background: "none", border: "none", color: "#E53935", cursor: "pointer", padding: 2, fontSize: 10 }}>✕</button>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Add stage */}
                  <div style={{ display: "flex", gap: 8, marginTop: 8, marginBottom: 20 }}>
                    <input value={newStageName} onChange={e => setNewStageName(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && addStage()}
                      placeholder="Add a stage..." style={{ ...inputStyle, flex: 1, padding: "8px 12px", fontSize: 12 }} />
                    <button onClick={addStage} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid " + "rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.04)", backdropFilter: "blur(16px) saturate(1.4)", WebkitBackdropFilter: "blur(16px) saturate(1.4)", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer" }}>+ Add</button>
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => { setSelectedTemplate(null); setPipelineStages([]); }} style={{ padding: "12px 20px", borderRadius: 8, border: "1px solid " + "rgba(217,219,221,0.12)", background: "transparent", color: "rgba(217,219,221,0.4)", fontSize: 12, cursor: "pointer" }}>
                      ← Back
                    </button>
                    <Btn primary onClick={savePipeline} style={{ flex: 1, padding: "12px", fontSize: 13, borderRadius: 8, justifyContent: "center", background: "#FAFAFA", color: "#2C2E33", border: "none" }}>
                      Save Pipeline & Continue →
                    </Btn>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════ STEP 2: CSV Import ════ */}
          {step === 3 && (
            <div>
              <div style={{ padding: "14px 16px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid " + "rgba(217,219,221,0.08)", marginBottom: 20 }}>
                <div style={{ fontSize: 13, color: "#FAFAFA", lineHeight: 1.6 }}>
                  Pipeline is ready! Now let's bring in your contacts. Upload a CSV from HubSpot, Salesforce, or any spreadsheet. I'll auto-match the columns for you.
                </div>
              </div>

              {!csvData ? (
                <div>
                  <input ref={fileInputRef} type="file" accept=".csv,.tsv,.txt" onChange={handleFileSelect} style={{ display: "none" }} />
                  <button onClick={() => fileInputRef.current?.click()} style={{
                    width: "100%", padding: "40px 20px", borderRadius: 8, border: "2px dashed " + "rgba(217,219,221,0.12)",
                    background: "rgba(255,255,255,0.04)", backdropFilter: "blur(16px) saturate(1.4)", WebkitBackdropFilter: "blur(16px) saturate(1.4)", cursor: "pointer", textAlign: "center", transition: "all 0.15s",
                  }}>
                    <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}><svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{opacity:0.4}}><path d="M3 6v12a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-7l-2-3H5a2 2 0 00-2 2z" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg></div>
                    <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA", marginBottom: 4 }}>Drop your CSV here or click to browse</div>
                    <div style={{ fontSize: 11, color: "rgba(217,219,221,0.4)" }}>Supports CSV and TSV from HubSpot, Salesforce, Zoho, Excel, Google Sheets</div>
                  </button>

                  <button onClick={() => { updateOnboardingStep(4); setStep(4); }} style={{
                    width: "100%", padding: "12px", borderRadius: 8, border: "1px solid " + "rgba(217,219,221,0.12)",
                    background: "transparent", color: "rgba(217,219,221,0.4)", fontSize: 12, cursor: "pointer", marginTop: 12,
                  }}>
                    Skip — I'll add contacts manually →
                  </button>
                </div>
              ) : (
                <div>
                  {/* File info */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8, background: "#2E8B57" + "10", border: "1px solid " + "#2E8B57" + "30", marginBottom: 16 }}>
                    <span style={{ fontSize: 16 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg></span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 400, color: "#FAFAFA" }}>{csvFile?.name}</div>
                      <div style={{ fontSize: 10, color: "rgba(217,219,221,0.4)" }}>{csvData.rows.length} contacts found • {csvData.headers.length} columns</div>
                    </div>
                    <button onClick={() => { setCsvData(null); setCsvFile(null); setColumnMapping({}); setImportResult(null); }}
                      style={{ background: "none", border: "none", color: "rgba(217,219,221,0.4)", cursor: "pointer", fontSize: 12 }}>✕ Change</button>
                  </div>

                  {/* Column mapping */}
                  <div style={{ fontSize: 11, fontWeight: 400, color: "rgba(217,219,221,0.4)", marginBottom: 8, letterSpacing: ".06em" }}>COLUMN MAPPING</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 16 }}>
                    {(() => {
                      // Each CSV column index can be claimed by at most one field.
                      // The current row's own selection is excluded from the set so
                      // it stays selectable in its own dropdown.
                      const usedColumns = new Set(Object.values(columnMapping).filter(v => typeof v === "number" && v >= 0));
                      const required = ["fn"];
                      return IMPORT_FIELDS.map(f => {
                        const currentColIdx = columnMapping[f.id];
                        return (
                          <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ fontSize: 11, fontWeight: 400, color: required.includes(f.id) ? "#FAFAFA" : "rgba(217,219,221,0.6)", width: 70, flexShrink: 0 }}>
                              {f.label}{required.includes(f.id) ? " *" : ""}
                            </div>
                            <Select value={currentColIdx ?? -1} onChange={e => setMapping(f.id, parseInt(e.target.value))}
                              style={{ flex: 1, padding: "6px 8px", borderRadius: 8, border: "1px solid " + (currentColIdx !== undefined ? "#2E8B57" + "60" : "rgba(217,219,221,0.12)"), background: "rgba(255,255,255,0.04)", backdropFilter: "blur(16px) saturate(1.4)", WebkitBackdropFilter: "blur(16px) saturate(1.4)", color: "#FAFAFA", fontSize: 11 }}>
                              <option value={-1}>— Skip —</option>
                              {csvData.headers.map((h, i) => {
                                const isUsedElsewhere = i !== currentColIdx && usedColumns.has(i);
                                return (
                                  <option key={i} value={i} disabled={isUsedElsewhere}>
                                    {h}{isUsedElsewhere ? " (already used)" : ""}
                                  </option>
                                );
                              })}
                            </Select>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  {/* Matched count */}
                  <div style={{ fontSize: 11, color: "#FAFAFA", fontWeight: 400, marginBottom: 12 }}>
                    ✓ {Object.keys(columnMapping).length} of {csvData.headers.length} columns auto-matched
                  </div>

                  {/* Preview */}
                  {importPreview.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 400, color: "rgba(217,219,221,0.4)", marginBottom: 6, letterSpacing: ".06em" }}>PREVIEW (first 5)</div>
                      <div style={{ borderRadius: 8, border: "1px solid " + "rgba(217,219,221,0.12)", overflow: "hidden" }}>
                        {importPreview.map((c, i) => (
                          <div key={i} style={{ display: "flex", gap: 12, padding: "8px 12px", borderBottom: i < importPreview.length - 1 ? "1px solid " + "rgba(217,219,221,0.12)" : "none", background: i % 2 ? "rgba(255,255,255,0.04)" : "#33363D", fontSize: 11 }}>
                            <span style={{ fontWeight: 400, color: "#FAFAFA", minWidth: 100 }}>{c.fn} {c.ln}</span>
                            <span style={{ color: "rgba(217,219,221,0.6)", flex: 1 }}>{c.company}</span>
                            <span style={{ color: "rgba(217,219,221,0.4)" }}>{c.email}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Import result */}
                  {importResult && (
                    <div style={{ padding: "12px 14px", borderRadius: 8, background: "#2E8B57" + "10", border: "1px solid " + "#2E8B57" + "30", marginBottom: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA" }}>✓ Imported {importResult.imported} contacts!</div>
                      {importResult.failed > 0 && <div style={{ fontSize: 11, color: "rgba(217,219,221,0.4)", marginTop: 4 }}>{importResult.failed} failed</div>}
                    </div>
                  )}

                  {/* Actions */}
                  {!importResult ? (
                    <Btn primary onClick={handleImport} style={{ width: "100%", padding: "14px", fontSize: 14, borderRadius: 8, justifyContent: "center", background: "#FAFAFA", color: "#2C2E33", border: "none" }}>
                      {importing ? `Importing ${csvData.rows.length} contacts...` : `Import ${csvData.rows.length} Contacts →`}
                    </Btn>
                  ) : (
                    <Btn primary onClick={() => { updateOnboardingStep(4); setStep(4); }}
                      style={{ width: "100%", padding: "14px", fontSize: 14, borderRadius: 8, justifyContent: "center", background: "#FAFAFA", color: "#2C2E33", border: "none" }}>
                      Continue →
                    </Btn>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ════ STEP 3: Ready ════ */}
          {step === 4 && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#00D68F15", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", border: "2px solid #00D68F30" }}>
                <span style={{ fontSize: 36 }}><svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M12 2C8 6 6 10 6 14l6 6c4 0 8-2 12-6-4-4-8-6-12-12z" stroke="#FAFAFA" strokeWidth="1.5" fill="none" strokeLinejoin="round"/><circle cx="12" cy="12" r="2" stroke="#FAFAFA" strokeWidth="1.5" fill="none"/></svg></span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 400, color: "#FAFAFA", marginBottom: 8 }}>You're Ready!</div>
              <div style={{ fontSize: 13, color: "rgba(217,219,221,0.5)", marginBottom: 8, lineHeight: 1.6 }}>
                Your CRM is set up with {pipelineStages.length > 0 ? pipelineStages.length + " pipeline stages" : "a default pipeline"}
                {importResult ? ` and ${importResult.imported} contacts` : ""}.
              </div>
              <div style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(217,219,221,0.08)", margin: "20px 0", textAlign: "left" }}>
                <div style={{ fontSize: 12, fontWeight: 400, color: "#FAFAFA", marginBottom: 6 }}>Pro tip from Core:</div>
                <div style={{ fontSize: 12, color: "rgba(217,219,221,0.5)", lineHeight: 1.6 }}>
                  Press the mic button or hit Space anywhere to talk to me. I can pull up contacts, draft emails, summarize your pipeline, and recommend who to call next. Head to Settings to set up your phone system and start calling from the browser.
                </div>
              </div>
              <Btn primary onClick={handleFinish} style={{ padding: "14px 40px", fontSize: 15, borderRadius: 8, background: "#FAFAFA", color: "#2C2E33", border: "none" }}>
                {loading ? "Loading..." : "Enter Coreflow →"}
              </Btn>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 32px", borderTop: "1px solid rgba(217,219,221,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 10, color: "rgba(217,219,221,0.3)" }}>Coreflow CRM</div>
          {step < 4 && <button onClick={handleFinish} style={{ background: "none", border: "none", fontSize: 10, color: "rgba(217,219,221,0.3)", cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>Skip setup — I'll do this later</button>}
        </div>
      </div>
    </div>
  );
}
